import type { ReactNode } from "react";

import { cn, Label, Select, SelectContent, SelectTrigger, SelectValue } from "@lagoon/ui";

/**
 * A labelled select.
 *
 * `FormField` cannot do this one. It associates a label with a control by
 * cloning the child and giving it an `id`, and the child here would be the
 * Radix `Select` root, which is a context provider rather than an element: the
 * `id` would land on nothing and the label would point at nothing. The id has
 * to reach `SelectTrigger`, which is the button the label is actually for.
 *
 * Worth stating rather than fixing in the library, because it is a real
 * asymmetry between an `<input>` and a composed control, and a `FormField`
 * that guessed which descendant to label would be worse than this.
 */
export function SelectField(props: {
  id: string;
  label: ReactNode;
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
  /** Shown in the trigger while nothing is chosen. */
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}): React.ReactElement {
  return (
    <div className={cn("lg:flex lg:flex-col lg:gap-2", props.className)}>
      <Label htmlFor={props.id}>{props.label}</Label>
      {/* `disabled` is spread rather than passed, because
          `exactOptionalPropertyTypes` makes an explicit `undefined` a
          different thing from an absent key and Radix declares it required. */}
      <Select
        value={props.value}
        onValueChange={props.onValueChange}
        {...(props.disabled === undefined ? {} : { disabled: props.disabled })}
      >
        <SelectTrigger id={props.id}>
          <SelectValue
            {...(props.placeholder === undefined ? {} : { placeholder: props.placeholder })}
          />
        </SelectTrigger>
        <SelectContent>{props.children}</SelectContent>
      </Select>
    </div>
  );
}
